import { action, observable, reaction } from 'mobx'
// Force Webpack recompile cache invalidation
import { VoteRequest } from '../requests/VoteRequest'
import authenticationStore from './authenticationStore'
import { apiUrl, apiWsUrl } from '../config'
import { Client } from '@stomp/stompjs'
import SockJS from 'sockjs-client'

class voteStore {
  @observable voteDetail = []

  // STOMP client state
  wsClient = null
  wsSubscriptions = {}
  _wsToken = ''

  constructor() {
    try {
      reaction(
        () => authenticationStore.appToken,
        () => {
          this.disconnectAll()
          this._ensureStompClient()
        }
      )
    } catch (e) {
      console.error('Failed to setup token reaction in voteStore', e)
    }
  }

  _ensureStompClient = () => {
    let currentToken = ''
    try {
      const appTokenStr = localStorage.getItem('jwt')
      if (appTokenStr) {
        currentToken = JSON.parse(appTokenStr).access_token || ''
      }
    } catch (e) {
      console.error('Failed to parse jwt from localStorage', e)
    }

    if (this.wsClient && this._wsToken && this._wsToken !== currentToken) {
      try {
        this.wsClient.deactivate()
      } catch (e) {}
      this.wsClient = null
      this.wsSubscriptions = {}
    }

    if (this.wsClient) return

    const token = currentToken
    const baseWsUrl = apiWsUrl || apiUrl
    const wsUrl = token
      ? `${baseWsUrl}/api/v1/newsfeed/ws?access_token=${token}`
      : `${baseWsUrl}/api/v1/newsfeed/ws`

    this._wsToken = token

    this.wsClient = new Client({
      webSocketFactory: () => new SockJS(wsUrl),
      reconnectDelay: 3000,
      onStompError: frame => {
        console.error('STOMP error in voteStore:', frame)
      },
      onConnect: () => {
        const activeCodes = Object.keys(this.wsSubscriptions)
        this.wsSubscriptions = {}
        activeCodes.forEach(code => this.subscribeRealtime(code))
      },
    })
    this.wsClient.activate()
  }

  @action subscribeRealtime = (workCode) => {
    if (!workCode) return
    if (this.wsSubscriptions[workCode]) return

    this._ensureStompClient()

    const subFn = () => {
      if (this.wsSubscriptions[workCode]) return
      try {
        const sub = this.wsClient.subscribe(
          `/topic/vote/${workCode}`,
          message => {
            console.log('[WebSocket] Vote updated for:', workCode)
            this.getVoteDetail(workCode)
          }
        )
        this.wsSubscriptions[workCode] = sub
      } catch (err) {
        console.error('Failed to subscribe to vote:', workCode, err)
      }
    }

    if (this.wsClient && this.wsClient.connected) {
      subFn()
    } else {
      setTimeout(() => {
        if (this.wsClient && this.wsClient.connected) {
          subFn()
        }
      }, 1000)
    }
  }

  @action unsubscribeRealtime = (workCode) => {
    if (!workCode) return
    const sub = this.wsSubscriptions[workCode]
    if (sub) {
      try {
        sub.unsubscribe()
      } catch (e) {}
      delete this.wsSubscriptions[workCode]
    }
  }

  @action disconnectAll = () => {
    Object.keys(this.wsSubscriptions).forEach(code => {
      this.unsubscribeRealtime(code)
    })
    if (this.wsClient) {
      try {
        this.wsClient.deactivate()
      } catch (e) {}
      this.wsClient = null
    }
  }

  @action createVote = data => {
    return new Promise((resolve, reject) => {
      VoteRequest.createVote(data)
        .then(response => {
          resolve(response)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  @action getVoteDetail = workCode => {
    return new Promise((resolve, reject) => {
      VoteRequest.getVoteDetail(workCode)
        .then(response => {
          this.voteDetail = response.data.users.map(user => ({
            ...user,
            permission: 'USER',
            assignee_type: 'USER',
          }))
          resolve(response)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  @action updatePersonVote = (userCode, data) => {
    return new Promise((resolve, reject) => {
      VoteRequest.updatePersonVote(userCode, data)
        .then(response => {
          resolve(response)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  @action updateVote = (userCode, data) => {
    return new Promise((resolve, reject) => {
      VoteRequest.updateVote(userCode, data)
        .then(response => {
          resolve(response)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  /** Clear store */
  @action clearStore = () => {
    this.voteDetail = []
  }
}

export default new voteStore()
